import type { NotificationState } from "./notifications.ts";
import type { GamePhase } from "./phase.ts";
import type { TotemId } from "./totem.ts";
import type { RandomEventEngineState } from "./randomEvent.ts";
import { createEmptyRandomEventState } from "./randomEvent.ts";
import type { Season } from "./season.ts";

export { createEmptyNotificationState } from "./notifications.ts";
export type {
  LocalizedNotificationKind,
  NotificationState,
  WorldNotification,
} from "./notifications.ts";
export { createEmptyRandomEventState };
export type { RandomEventEngineState } from "./randomEvent.ts";

export const WORLD_STATE_SCHEMA_VERSION = 1;

export type TileKey = `${number},${number}`;

export interface TileCoordinates {
  q: number;
  r: number;
}

export const TILE_TERRAIN_IDS = [
  "desert",
  "mountain",
  "swamp",
  "forest",
  "plains",
  "river",
] as const;

export type TileTerrain = (typeof TILE_TERRAIN_IDS)[number];

export function isTileTerrain(value: unknown): value is TileTerrain {
  return (
    typeof value === "string" &&
    (TILE_TERRAIN_IDS as readonly string[]).includes(value)
  );
}

export const TILE_VISIBILITY_IDS = ["hidden", "revealed"] as const;

export type TileVisibility = (typeof TILE_VISIBILITY_IDS)[number];

export function isTileVisibility(value: unknown): value is TileVisibility {
  return (
    typeof value === "string" &&
    (TILE_VISIBILITY_IDS as readonly string[]).includes(value)
  );
}

export const WEATHER_CONDITIONS = [
  "clear",
  "cloudy",
  "overcast",
  "rainy",
  "snowy",
] as const;

export type Weather = (typeof WEATHER_CONDITIONS)[number];

export function isWeather(value: unknown): value is Weather {
  return (
    typeof value === "string" &&
    (WEATHER_CONDITIONS as readonly string[]).includes(value)
  );
}

export interface TileData {
  q: number;
  r: number;
  terrain: TileTerrain;
  visibility: TileVisibility;
  isStartingTile: boolean;
  foodDepletion: number;
}

export interface ClanState {
  clanName: string;
  totemId: TotemId;
  population: number;
  foodSupplies: number;
  currentTile: TileCoordinates;
  foodBankStreak: number;
  famineStreak: number;
  ambientWeather: Weather;
  movedThisTurn: boolean;
}

export interface RngState {
  seed: number;
  state: number;
}

export interface WorldState {
  schemaVersion: number;
  phase: GamePhase;
  turn: number;
  season: Season;
  clan: ClanState;
  tiles: Record<TileKey, TileData>;
  rng: RngState;
  notifications: NotificationState;
  randomEvent: RandomEventEngineState;
}

export function tileKey(q: number, r: number): TileKey {
  return `${q},${r}`;
}
